import apiClient from '@/api/apiClient';
import type { PaginatedResponse } from '@/types/api';

/** Sortable columns of the monitored-entity listing; mirrors the backend whitelist. */
export type MonitoringEntitySortField = 'tax_id_full' | 'entity_type' | 'inserted_at' | 'deleted_at';

/** Why a submitted document was not added. Mirrors the backend's codes. */
export type MonitoringRejectionStatus = 'already_monitored' | 'same_company' | 'not_found';

export type MonitoringEntityRejection = {
  tax_id: string;
  status: MonitoringRejectionStatus;
  /** The document already monitoring that entity; absent when there is no company behind it. */
  existing_tax_id_full: string | null;
};

export type MonitoringEntityBatchAddResult = {
  added: number;
  skipped: number;
  invalid: number;
  details: MonitoringEntityRejection[];
};

export type MonitoringEntityPreflight = {
  addable: number;
  details: MonitoringEntityRejection[];
};

export type MonitoringEntityListParams = {
  show_deleted?: boolean;
  deleted_only?: boolean;
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: MonitoringEntitySortField;
  sort_desc?: boolean;
};


export type MonitoringListParams = {
  is_template?: boolean;
  page?: number;
  page_size?: number;
  name?: string;
  sort_by?: string;
  sort_desc?: boolean;
  show_deleted?: boolean;
  deleted_only?: boolean;
  /** Production state; omit to list policies in and out of production. */
  is_active?: boolean;
};

const monitoringApi = {
  async getOptions(language: string = 'pt-BR'): Promise<any> {
    try {
      return await apiClient.get(
        `/account-admin/config/kyc-monitoring-options?language=${encodeURIComponent(language)}`,
      );
    } catch (error) {
      console.error('Get Monitoring Config Options:', error);
      throw error;
    }
  },

  async getPolicies(params: MonitoringListParams = {}): Promise<PaginatedResponse<any>> {
    try {
      return await apiClient.get('/account-admin/monitoring/policies/', { params });
    } catch (error) {
      console.error('Get Monitoring Policies:', error);
      throw error;
    }
  },

  async getPolicy(id: string): Promise<any> {
    try {
      return await apiClient.get(`/account-admin/monitoring/policies/${id}`);
    } catch (error) {
      console.error('Get Monitoring Policy:', error);
      throw error;
    }
  },

  async createPolicy(data: {
    versioned_group_name: string;
    is_template: boolean;
    is_active: boolean;
    entity_type: string[];
    entity_relationship_scopes?: string[];
    checks: any[];
    should_promote?: boolean;
  }): Promise<any> {
    try {
      return await apiClient.post('/account-admin/monitoring/policies/', data);
    } catch (error) {
      console.error('Create Monitoring Policy:', error);
      throw error;
    }
  },

  async updatePolicy(id: string, data: { versioned_group_name: string }): Promise<any> {
    try {
      return await apiClient.put(`/account-admin/monitoring/policies/${id}`, data);
    } catch (error) {
      console.error('Update Monitoring Policy:', error);
      throw error;
    }
  },

  async updateInstance(policyId: string, instanceId: string, data: any): Promise<any> {
    try {
      return await apiClient.put(
        `/account-admin/monitoring/policies/${policyId}/instances/${instanceId}`,
        data,
      );
    } catch (error) {
      console.error('Update Monitoring Instance:', error);
      throw error;
    }
  },

  async createDraft(policyId: string, baseInstanceId?: string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/draft`, {
        base_instance_id: baseInstanceId ?? null,
      });
    } catch (error) {
      console.error('Create Monitoring Draft:', error);
      throw error;
    }
  },

  async discardDraft(policyId: string): Promise<any> {
    try {
      return await apiClient.delete(`/account-admin/monitoring/policies/${policyId}/draft`);
    } catch (error) {
      console.error('Discard Monitoring Draft:', error);
      throw error;
    }
  },

  async promoteToProduction(policyId: string, instanceId: string): Promise<any> {
    try {
      return await apiClient.post(
        `/account-admin/monitoring/policies/${policyId}/instances/${instanceId}/promote`,
        {},
      );
    } catch (error) {
      console.error('Promote Monitoring Instance:', error);
      throw error;
    }
  },

  async toggleActive(policyId: string, isActive: boolean): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/toggle-active`, {
        is_active: isActive,
      });
    } catch (error) {
      console.error('Toggle Monitoring Policy Active:', error);
      throw error;
    }
  },

  async deletePolicy(policyId: string): Promise<any> {
    try {
      return await apiClient.delete(`/account-admin/monitoring/policies/${policyId}`);
    } catch (error) {
      console.error('Delete Monitoring Policy:', error);
      throw error;
    }
  },

  async listPolicyEntities(
    policyId: string,
    params: MonitoringEntityListParams = {},
  ): Promise<any> {
    try {
      return await apiClient.get(`/account-admin/monitoring/policies/${policyId}/entities`, { params });
    } catch (error) {
      console.error('List Policy Entities:', error);
      throw error;
    }
  },

  /** Downloads every entity matching `params` (all pages) as a CSV file built by the backend. */
  async exportPolicyEntities(
    policyId: string,
    params: Omit<MonitoringEntityListParams, 'page' | 'page_size'> = {},
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const res: Response = await apiClient.get(
        `/account-admin/monitoring/policies/${policyId}/entities/export`,
        { params, rawResponse: true },
      );
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') ?? '';
      const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `entidades-monitoradas-${policyId}.csv`;
      return { blob, filename };
    } catch (error) {
      console.error('Export Policy Entities:', error);
      throw error;
    }
  },

  /** What a batch-add of this list would do, without writing anything. */
  async preflightEntities(
    policyId: string,
    entities: { tax_id: string; entity_type: string }[],
  ): Promise<MonitoringEntityPreflight> {
    try {
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/entities/preflight`, {
        entities,
      });
    } catch (error) {
      console.error('Preflight Entities:', error);
      throw error;
    }
  },

  async batchAddEntities(
    policyId: string,
    entities: { tax_id: string; entity_type: string }[],
  ): Promise<MonitoringEntityBatchAddResult> {
    try {
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/entities/batch-add`, {
        entities,
      });
    } catch (error) {
      console.error('Batch Add Entities:', error);
      throw error;
    }
  },

  /** Ids of every active entity matching `search`, so "select all" can reach every page. */
  async listPolicyEntityIds(policyId: string, search?: string): Promise<string[]> {
    try {
      const res = await apiClient.get(`/account-admin/monitoring/policies/${policyId}/entities/ids`, {
        params: { search },
      });
      return res?.ids ?? [];
    } catch (error) {
      console.error('List Policy Entity Ids:', error);
      throw error;
    }
  },

  async batchRemoveEntities(policyId: string, entityIds: string[]): Promise<any> {
    try {
      return await apiClient.post(
        `/account-admin/monitoring/policies/${policyId}/entities/batch-remove`,
        { entity_ids: entityIds },
      );
    } catch (error) {
      console.error('Batch Remove Entities:', error);
      throw error;
    }
  },

  /** Empties the policy — every monitored entity, regardless of the current filter. */
  async removeAllEntities(policyId: string): Promise<any> {
    try {
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/entities/remove-all`, {});
    } catch (error) {
      console.error('Remove All Entities:', error);
      throw error;
    }
  },

  async uploadEntitiesFile(policyId: string, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiClient.post(`/account-admin/monitoring/policies/${policyId}/entities/upload-file`, formData);
    } catch (error) {
      console.error('Upload Entities File:', error);
      throw error;
    }
  },
};

export default monitoringApi;
